import { SpendGate } from '../../shared/contracts/spend-gate';
import {
  hasVoyageToiFinished,
  voyageCreditsRemaining,
  voyageSensorSpendGate,
  voyageSpendRefusal,
  VoyageSpendAction,
} from '../voyage-credits.model';
import { actionCredits } from 'src/app/services/operators/voyageradar-credit-actions';

/**
 * Voyage Radar's answer to `SpendGate` — a thin adapter over the credit rules
 * that already exist in `voyage-credits.model.ts`. No rule lives here: pricing
 * comes from the sensor's own credit catalogue (`actionCredits`), the refusal
 * ladder from `voyageSpendRefusal`, and the balance from the sensor document —
 * this class only phrases them in the provider-neutral contract shared code
 * speaks (D1, REFACTOR-PLAN.md §6.4).
 *
 * Constructed with a THUNK rather than a sensor: the open sensor changes with
 * the collection, and the map controller resolves it per call today
 * (`this.sensor` is a getter over `host.collection`). Capturing a sensor
 * reference at construction would freeze the gate to whichever task was open
 * when `attach()` ran — the same staleness `syncCreditLines` exists to prevent.
 *
 * Narrowed to `VoyageSpendAction`, which keeps this file cast-free: shared
 * callers hold the default `SpendGate` and the assignment is sound because the
 * action keys shared features pass ('portCalls', 'nearbyVessels', …) ARE
 * Voyage Radar's action keys — the catalogue in
 * `voyageradar-credit-actions.ts` is where both sides get them from.
 *
 * Not yet consumed: phase 2's `spend-broadcast` and the phase 5/6 feature
 * extractions are the callers. Introduced first (phase 0) so every feature is
 * written against this seam rather than against the credits model directly.
 */
export class VrSpendGateway implements SpendGate<VoyageSpendAction> {
  constructor(private readonly sensor: () => any) {}

  /** The sensor's own catalogue price; `actionCredits` supplies the shipped fallback. */
  priceFor(action: VoyageSpendAction): number | null {
    return actionCredits(this.sensor(), action);
  }

  /**
   * THE pre-flight for a paid action — same order everywhere: the TOI closes
   * the shop, and only inside an open shop does the balance matter.
   */
  refuse(action: VoyageSpendAction, cost: number): string | null {
    return voyageSpendRefusal(voyageSensorSpendGate(this.sensor(), action, cost));
  }

  remaining(): number | null {
    return voyageCreditsRemaining(this.sensor());
  }

  spendingClosed(): boolean {
    return hasVoyageToiFinished(this.sensor());
  }

  /**
   * Mirror the server's counter onto the local sensor doc — success or failure
   * alike, since a 402/403 body carries the balance too. The stored counter IS
   * the balance; every price line re-reads it via `remaining()`.
   *
   * DELIBERATELY NOT the whole of what the monolith does after a spend. Every
   * `apiCreditsRemaining` write in `voyage-map.controller.ts` is coupled with a
   * `syncCreditLines()` call that pushes the new balance into every priced
   * drawer — and that broadcast is the CALLER's job, not this gateway's, which
   * knows the sensor and nothing else. Phase 2's `spend-broadcast` owns it; a
   * feature calling `recordSpend` without re-broadcasting leaves every price
   * line stale, which is the exact bug `syncCreditLines` exists to prevent.
   */
  recordSpend(response: any): void {
    const sensor = this.sensor();
    if (!sensor) return;
    const remaining =
      response?.apiCreditsRemaining ?? response?.error?.apiCreditsRemaining;
    if (remaining != null) sensor.apiCreditsRemaining = remaining;
  }
}
